import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import {
  setTutEditMode,
  setTutCurrentItem,
} from '../../../redux/slices/tutorialEditSlice';
import { ImageBaseURL } from '../../../utilities/api/axios';
import {
  DeleteIcon,
  EditPencilIcon,
  LineUpIcon,
} from '../../../Icons/DashboardIcons';
import fallbackImg from '../../../utilities/dummy_assets/login.jpg';
import AdminModuleAPIs from '../../../utilities/api/tutorials/AdminModuleAPIs';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from 'react-toastify'
const GridTutorialCard = ({ item }) => {
  const dispatch = useDispatch();
  const [isHovering, setIsHovering] = useState(false);
  const getImageUrl = img => {
    if (!img) return fallbackImg;

    if (img.startsWith("http"))
      return img;

    if (img.startsWith("/media"))
      return `${ImageBaseURL}${img}`;

    if (img.startsWith("media"))
      return `${ImageBaseURL}/${img}`;

    return `${ImageBaseURL}/media/${img}`;
  };

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '16px',
        border: '1px solid #F1F5F9',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderColor: '#E2E8F0',
        },
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Image Container with Floating Icons */}
      <Box sx={{ position: 'relative', overflow: 'hidden', p: 1.5, pb: 0 }}>
        <CardMedia
          component="img"
          height="190"
          image={getImageUrl(item.image)}
          alt={item.name || 'Tutorial cover'}
          onError={e => {
            e.currentTarget.src = fallbackImg;
            e.currentTarget.onerror = null;
          }}
          sx={{
            display: 'block',
            objectFit: 'cover',
            width: '100%',
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'transform 0.5s ease',
            '&:hover': {
              transform: 'scale(1.03)',
            },
          }}
          onClick={() => {
            dispatch(setTutCurrentItem(item));
          }}
        />
        {/* Glassmorphic Top Right Icons */}
        <Box
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            display: "flex",
            gap: 1.5,
            opacity: isHovering ? 1 : 0.8,
            transition: 'opacity 0.2s ease',
          }}
        >
          {/* INFO ICON */}
          <Tooltip title="View Details" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: 'blur(8px)',
                borderRadius: "10px",
                height: "38px",
                width: "38px",
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                "&:hover": {
                  backgroundColor: "#fff",
                  transform: 'scale(1.1)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setTutEditMode(false));
                dispatch(setTutCurrentItem(item));
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 20, color: '#475569' }} />
            </IconButton>
          </Tooltip>

          {/* EDIT ICON */}
          <Tooltip title="Edit Module" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: 'blur(8px)',
                borderRadius: "10px",
                height: "38px",
                width: "38px",
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                "&:hover": {
                  backgroundColor: "#fff",
                  transform: 'scale(1.1)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setTutEditMode(true));
                dispatch(setTutCurrentItem(item));
              }}
            >
              <EditPencilIcon width="18" height="18" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, p: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#0F172A',
            fontSize: '18px',
            lineHeight: 1.3,
            textAlign: 'left',
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748B',
            fontSize: '14px',
            lineHeight: 1.5,
            textAlign: 'left',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {item.description || item.topic || 'No description available for this tutorial.'}
        </Typography>
      </CardContent>

      {/* Action Icons - Bottom */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          p: 2,
          pt: 0,
        }}
      >
        <Tooltip title="View Stats" arrow>
          <IconButton
            size="small"
            sx={{
              p: '8px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              "&:hover": {
                backgroundColor: "#F1F5F9",
                borderColor: '#CBD5E1',
              },
            }}
          >
            <LineUpIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Module" arrow>
          <IconButton
            size="small"
            sx={{
              p: '8px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              "&:hover": {
                backgroundColor: "#FEF2F2",
                borderColor: '#FCA5A5',
              },
            }}
            onClick={async (e) => {
              e.stopPropagation();
              const confirmDelete = window.confirm(
                `Are you sure you want to delete "${item.name}"?`
              );
              if (!confirmDelete) return;
              try {
                await AdminModuleAPIs.DeleteModule(item.id);
                toast.success("Module deleted successfully");
                window.location.reload();
              } catch (error) {
                toast.error("Failed to delete module");
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

export default GridTutorialCard;

// import React from 'react';
// import { useDispatch } from 'react-redux';
// import { Card, CardMedia, CardContent, Typography, Box, Button } from '@mui/material';
// import EditIcon from '@mui/icons-material/Edit';
// import { setTutEditMode } from '../../../redux/slices/tutorialEditSlice'; // Correct path

// const styles = {
//   card: {
//     position: 'relative',
//   },
//   mediaContainer: {
//     position: 'relative',
//   },
//   cardMedia: {
//     width: '100%',
//   },
//   textContainer: {
//     position: 'absolute',
//     left: 0,
//     right: 0,
//     bottom: 0,
//     height: '50%',
//     background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
//     color: 'white',
//     display: 'flex',
//     flexDirection: 'column',
//     justifyContent: 'flex-end',
//     alignItems: 'flex-start',
//     opacity: 0,
//     transition: 'opacity 0.3s ease-in-out',
//     padding: '10px',
//     boxSizing: 'border-box',
//   },
//   textVisible: {
//     opacity: 1,
//   },
//   description: {},
//   mentor: {},
// };

// const GridTutorialCard = ({ item }) => {
//   const dispatch = useDispatch();

//   return (
//     <Card sx={styles.card}>
//       <Box
//         sx={styles.mediaContainer}
//         onMouseEnter={(e) => {
//           e.currentTarget.querySelector('.textContainer').style.opacity = 1;
//         }}
//         onMouseLeave={(e) => {
//           e.currentTarget.querySelector('.textContainer').style.opacity = 0;
//         }}
//       >
//         <CardMedia
//           component="img"
//           height="200"
//           image={item.image}
//           alt={item.topic}
//           sx={styles.cardMedia}
//         />
//         <Box className="textContainer" sx={styles.textContainer}>
//           <Typography variant="body2" color="inherit" sx={styles.description}>
//             {item.description}
//           </Typography>
//           <Typography variant="subtitle2" color="inherit" sx={styles.mentor}>
//             Mentor: {item.mentor}
//           </Typography>
//         </Box>
//         <Button
//           variant="contained"
//           sx={{
//             position: 'absolute',
//             minWidth: '0px',
//             top: 8,
//             right: 0,
//             color: 'white',
//             backgroundColor: 'transparent',
//             boxShadow: 'none',
//             '&:hover': {
//               backgroundColor: 'rgba(0, 0, 0, 0.1)',
//             },
//           }}
//           startIcon={<EditIcon />}
//           onClick={() => {
//             console.log('Edit button clicked'); // Add console log here
//             dispatch(setTutEditMode(true));
//           }}
//         >
//           Edit
//         </Button>
//       </Box>
//       <CardContent>
//         <Typography variant="h6" component="div">
//           {item.topic}
//         </Typography>
//       </CardContent>
//     </Card>
//   );
// };

// export default GridTutorialCard;
